import { useMemo } from "react";
import { motion } from "motion/react";

interface BlurTextProps {
  text: string;
  /** Stagger between segments, in ms. */
  delay?: number;
  className?: string;
  animateBy?: "words" | "letters";
}

/**
 * ReactBits BlurText — reveals text segment-by-segment with a blur + fade + rise.
 */
export default function BlurText({
  text,
  delay = 90,
  className = "",
  animateBy = "words",
}: BlurTextProps) {
  const segments = useMemo(
    () => (animateBy === "words" ? text.split(" ") : Array.from(text)),
    [text, animateBy],
  );

  return (
    <span className={className} style={{ display: "inline-flex", flexWrap: "wrap", justifyContent: "inherit" }}>
      {segments.map((seg, i) => (
        <motion.span
          key={`${seg}-${i}`}
          initial={{ filter: "blur(8px)", opacity: 0, y: 10 }}
          animate={{ filter: "blur(0px)", opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: (i * delay) / 1000, ease: [0.25, 0.46, 0.45, 0.94] }}
          style={{ display: "inline-block", willChange: "transform, filter, opacity" }}
        >
          {seg}
          {animateBy === "words" && i < segments.length - 1 ? " " : ""}
        </motion.span>
      ))}
    </span>
  );
}
